import { useId, useMemo, useState } from 'react';

import { useContactFieldSuggestions } from '@/api/queries/contactFieldSuggestions';
import type { BulkEditFields } from '@/api/queries/contacts';
import { useModal } from '@/components/modal/useModal';
import { Button, ChipEditor, Field, Icon, IconButton, Input, Select } from '@/components/ui';

interface BulkChangeModalProps {
  /** Number of contacts the change will apply to (for the header). */
  count: number;
  /**
   * Applies the assembled changes. Resolves once the operation was started
   * (set mode → background job) or completed (id mode). Must reject on failure
   * so the modal can surface the error; on success it closes/replaces the modal.
   */
  onApply: (fields: BulkEditFields) => Promise<void>;
}

type StatusChoice = '' | 'active' | 'inactive';
type DoNotContactChoice = '' | 'set' | 'clear';

/**
 * "Bulk change" dialog for the contacts grid — sets profile fields, rating and
 * status across the selected contacts, and adds tags/sources without touching
 * the ones a contact already carries. Only the fields the user fills in are sent.
 */
export function BulkChangeModal({ count, onApply }: BulkChangeModalProps) {
  const { closeModal } = useModal();

  const interestSug = useContactFieldSuggestions('profile.interest');
  const occupationSug = useContactFieldSuggestions('profile.occupation');
  const positionSug = useContactFieldSuggestions('profile.position');
  const specializationSug = useContactFieldSuggestions('profile.specialization');
  const tagSug = useContactFieldSuggestions('tags');
  const sourceSug = useContactFieldSuggestions('sources');

  const [interest, setInterest] = useState('');
  const [occupation, setOccupation] = useState('');
  const [position, setPosition] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [rating, setRating] = useState('');
  const [status, setStatus] = useState<StatusChoice>('');
  const [doNotContact, setDoNotContact] = useState<DoNotContactChoice>('');
  const [tags, setTags] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Assemble the payload from just the fields the user touched. Scalars are set;
  // tags/sources are union-added server-side.
  const fields = useMemo<BulkEditFields>(() => {
    const f: BulkEditFields = {};
    if (interest.trim()) f.interest = interest.trim();
    if (occupation.trim()) f.occupation = occupation.trim();
    if (position.trim()) f.position = position.trim();
    if (specialization.trim()) f.specialization = specialization.trim();
    const r = Number(rating);
    if (rating.trim() !== '' && !Number.isNaN(r)) f.rating = r;
    if (status === 'active') f.active = true;
    else if (status === 'inactive') f.active = false;
    if (doNotContact === 'set') f.doNotContact = true;
    else if (doNotContact === 'clear') f.doNotContact = false;
    if (tags.length) f.addTags = tags;
    if (sources.length) f.addSources = sources;
    return f;
  }, [interest, occupation, position, specialization, rating, status, doNotContact, tags, sources]);

  const changeCount = Object.keys(fields).length;
  const hasChanges = changeCount > 0;

  const submit = async () => {
    if (!hasChanges || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onApply(fields);
      // onApply closes or replaces this modal on success; leave state untouched.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk change failed');
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="ds-modal-head">
        <h2>Bulk change</h2>
        <span className="ds-modal-sub">
          {count.toLocaleString()} contact{count === 1 ? '' : 's'}
        </span>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={closeModal} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <Banner>
            Only the fields you fill in are changed. Tags and sources are added — values a
            contact already has are kept.
          </Banner>

          <SuggestField
            label="Interest"
            value={interest}
            onChange={setInterest}
            suggestions={interestSug.suggestions}
          />
          <SuggestField
            label="Occupation"
            value={occupation}
            onChange={setOccupation}
            suggestions={occupationSug.suggestions}
          />
          <SuggestField
            label="Position"
            value={position}
            onChange={setPosition}
            suggestions={positionSug.suggestions}
          />
          <SuggestField
            label="Specialization"
            value={specialization}
            onChange={setSpecialization}
            suggestions={specializationSug.suggestions}
          />

          <Field label="Rating">
            <Input
              type="number"
              value={rating}
              placeholder="No change"
              min={0}
              max={100}
              onChange={(e) => setRating(e.target.value)}
            />
          </Field>

          <Field label="Status">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusChoice)}
              options={[
                { value: '', label: 'No change' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </Field>

          <Field label="Do not contact">
            <Select
              value={doNotContact}
              onChange={(e) => setDoNotContact(e.target.value as DoNotContactChoice)}
              options={[
                { value: '', label: 'No change' },
                { value: 'set', label: 'Set' },
                { value: 'clear', label: 'Clear' },
              ]}
            />
          </Field>

          <Field label="Add tags">
            <ChipEditor
              values={tags}
              onChange={setTags}
              suggestions={tagSug.suggestions}
              addLabel="Add tag"
              tone="brand"
            />
          </Field>
          <Field label="Add sources">
            <ChipEditor
              values={sources}
              onChange={setSources}
              suggestions={sourceSug.suggestions}
              addLabel="Add source"
              tone="teal"
            />
          </Field>

          {error && <Banner tone="error">{error}</Banner>}
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!hasChanges}
          loading={submitting}
          leftIcon={!submitting ? <Icon name="check" size={13} /> : undefined}
          onClick={() => void submit()}
        >
          {hasChanges ? `Apply changes (${changeCount})` : 'Apply changes'}
        </Button>
      </div>
    </>
  );
}

function SuggestField({
  label,
  value,
  onChange,
  suggestions,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  suggestions: string[];
}) {
  const listId = useId();
  return (
    <Field label={label}>
      <Input
        value={value}
        placeholder="No change"
        list={suggestions.length ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </Field>
  );
}

function Banner({ children, tone = 'info' }: { children: React.ReactNode; tone?: 'info' | 'error' }) {
  const isError = tone === 'error';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        background: isError ? 'var(--red-soft)' : 'var(--brand-soft)',
        color: isError ? 'var(--red)' : 'var(--brand-ink)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <Icon name={isError ? 'alert' : 'info'} size={14} style={{ marginTop: 1, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}
